export const internshipSteps = [
  'fac',
  'sem',
  'divida',
  'res',
  'eu',
  'end',
  'civil',
  'traj',
  'planos',
  'fin',
  'resp',
  'r1',
  'r2',
  'r3',
  'r4',
  'rlist',
  'av',
  'g1',
  'g2',
  'g3',
  'g4',
  'scr',
  'pre',
  'docs',
  'carta',
  'analise',
  'proposta',
  'assinar',
  'fim',
] as const

export type InternshipStep = (typeof internshipSteps)[number]

/** Etapas do cadastro de quem garante o financiamento: responsável (r) ou avalista (g). */
export type BackerRole = 'r' | 'g'

const simulationSteps = new Set<InternshipStep>(['fac', 'sem', 'divida', 'res'])
export const studentSteps = [
  'eu',
  'end',
  'civil',
  'traj',
  'planos',
  'fin',
] as const
export const responsibleEditor = ['r1', 'r2', 'r3', 'r4'] as const
export const guarantorEditor = ['g1', 'g2', 'g3', 'g4'] as const
const responsibleSteps = new Set<InternshipStep>(responsibleEditor)
const guarantorSteps = new Set<InternshipStep>(guarantorEditor)

export const MAX_GUARANTORS = 4

export function stageOf(step: InternshipStep) {
  if (simulationSteps.has(step)) return 0
  if (step === 'scr' || step === 'pre' || step === 'docs' || step === 'carta')
    return 2
  if (step === 'analise') return 3
  if (step === 'proposta' || step === 'assinar' || step === 'fim') return 4
  return 1
}

export function editorRole(step: InternshipStep): BackerRole | null {
  if (responsibleSteps.has(step)) return 'r'
  if (guarantorSteps.has(step)) return 'g'
  return null
}

export type InternshipFacts = {
  hasSimulation: boolean
  status: string
  serverStep: string
  /** Etapas da faculdade e do estudante já salvas no financial. */
  completed: Partial<Record<InternshipStep, boolean>>
  selfResponsible: boolean
  responsibleCount: number
  guarantorCount: number
  /** Cadastro aberto agora: um responsável ou avalista novo ou em edição. */
  editing: BackerRole | null
  hasDocuments: boolean
}

export function visibleSteps(facts: InternshipFacts) {
  return internshipSteps.filter((step) => {
    if (responsibleSteps.has(step)) {
      return (
        !facts.selfResponsible &&
        (facts.editing === 'r' || facts.responsibleCount === 0)
      )
    }
    if (step === 'rlist') return !facts.selfResponsible
    if (guarantorSteps.has(step)) return facts.editing === 'g'
    if (step === 'pre') {
      return facts.serverStep === 'pre_analysis' && facts.status === 'analysing'
    }
    return true
  })
}

export function resumeStep(facts: InternshipFacts): InternshipStep {
  if (facts.status === 'done') return 'fim'
  if (facts.status === 'denied') return 'analise'
  if (
    facts.serverStep === 'agreement_signature' ||
    facts.serverStep === 'credit_note_signature'
  ) {
    return 'assinar'
  }
  if (facts.serverStep === 'proposal' || facts.status === 'approved')
    return 'proposta'
  if (facts.serverStep === 'pre_analysis') {
    return facts.status === 'analysing' ? 'pre' : 'scr'
  }
  if (
    facts.status === 'analysing' ||
    facts.serverStep === 'committee_analysis' ||
    facts.serverStep === 'documents_analysis'
  ) {
    return 'analise'
  }
  if (facts.serverStep === 'proponets_documents' || facts.status === 'review') {
    return facts.hasDocuments ? 'carta' : 'docs'
  }
  if (!facts.hasSimulation) return facts.completed.fac ? 'res' : 'fac'

  const student = studentSteps.find((step) => !facts.completed[step])
  if (student) return student
  if (
    facts.selfResponsible ? !facts.completed.resp : facts.responsibleCount === 0
  ) {
    return 'resp'
  }
  return 'av'
}

/** Até onde a pessoa já chegou: ela volta a qualquer etapa anterior, nunca pula à frente. */
function reachable(
  step: InternshipStep,
  resume: InternshipStep,
  facts: InternshipFacts,
) {
  const order = internshipSteps as readonly InternshipStep[]
  const at = (item: InternshipStep) => order.indexOf(item)
  if (responsibleSteps.has(step)) return at(resume) >= at('resp')
  if (guarantorSteps.has(step)) return at(resume) >= at('av')
  if (step === 'pre') return resume === 'pre'
  if (step === 'docs' || step === 'carta') return at(resume) >= at('docs')
  if (step === 'rlist')
    return facts.responsibleCount > 0 && at(resume) >= at('resp')
  if (simulationSteps.has(step) && !facts.hasSimulation) {
    return step === 'fac' || Boolean(facts.completed.fac)
  }
  return at(step) <= at(resume)
}

export function resolveStep(
  requested: string | undefined,
  facts: InternshipFacts,
): InternshipStep {
  const resume = resumeStep(facts)
  const requestedStep = visibleSteps(facts).find((step) => step === requested)
  if (!requestedStep) return resume
  return reachable(requestedStep, resume, facts) ? requestedStep : resume
}

/**
 * Para onde o "Voltar" leva. A lista de responsáveis e a tela de avalistas voltam para a
 * seção anterior, e o início de um cadastro volta para a tela que o abriu,
 * sem passar pelas telas do cadastro de outra pessoa.
 */
export function previousStep(
  step: InternshipStep,
  facts: InternshipFacts,
): InternshipStep | null {
  const responsibles: InternshipStep = facts.selfResponsible ? 'resp' : 'rlist'
  switch (step) {
    case 'r1':
      return facts.responsibleCount > 0 ? 'rlist' : 'resp'
    case 'rlist':
      return 'resp'
    case 'av':
      return responsibles
    case 'g1':
      return 'av'
  }
  const steps = visibleSteps(facts)
  const index = steps.indexOf(step)
  return index > 0 ? steps[index - 1] : null
}

export function stepProgress(step: InternshipStep, facts: InternshipFacts) {
  const inStage = visibleSteps(facts).filter(
    (item) => stageOf(item) === stageOf(step),
  )
  const index = Math.max(inStage.indexOf(step), 0)
  return { current: index + 1, total: inStage.length }
}

export const studentDocuments = [
  'personal',
  'proof_of_registration',
  'college_records',
  'address',
] as const

export const backerDocuments = [
  'personal',
  'address',
  'proof_of_income',
  'income_tax_return',
] as const

export const ENABLING_LETTER = 'enabling_letter'

/** Renda mínima do avalista: 3 vezes a maior parcela da simulação. */
export function minGuarantorIncome(installments: number[]) {
  return installments.length ? 3 * Math.max(...installments) : 0
}
